import { IErrorApiResponse, IOrder } from '@wyz/types';
import { DateUtils } from '@wyz/utils';
import { isNil } from 'lodash-es';
import {
  IAcceptOrderItemPayload,
  IAcceptOrderValidationErrorResponse,
  ISuggestedOffer,
  PurchaseErrorCodeKeys,
} from '~/purchase/types';

export function applyOfferChanges(
  error: IErrorApiResponse<
    IAcceptOrderValidationErrorResponse,
    PurchaseErrorCodeKeys
  >,
  previousItems: Record<string, Array<IAcceptOrderItemPayload>>,
): Record<string, Array<IAcceptOrderItemPayload>> {
  const errorsBySupplierKey = error.errors.reduce(
    (acc, error) => {
      const byEan = {
        ...(acc[error.supplierId] || {}),
        [error.ean]: error,
      };

      return {
        ...acc,
        [error.supplierId]: byEan,
      };
    },
    {} as Record<string, Record<string, (typeof error.errors)[number]>>,
  );
  return Object.entries(previousItems).reduce((acc, [ean, items]) => {
    return {
      ...acc,
      [ean]: items.map((item) => {
        const error = errorsBySupplierKey[item.supplierId]?.[item.ean];
        const patch: Partial<typeof item> = {};
        if (error?.deliveryDateChanged) {
          patch.deliveryDate = error.actual.deliveryDate;
        }
        if (error?.priceChanged) {
          patch.sellingPrice = error.actual.sellingPrice;
        }
        if (error?.quantityChanged) {
          patch.quantity = error.actual.availableQuantity;
        }
        return {
          ...item,
          ...patch,
        };
      }),
    };
  }, {});
}

export const getMappedChanges = (
  suggestedOffers: ISuggestedOffer[],
  changes: IAcceptOrderValidationErrorResponse[],
  order: IOrder,
  orderItemIndex: number,
) => {
  const mappedChanges = new Map<string, IAcceptOrderValidationErrorResponse>();
  // in that case, we check diff between suggested offers and current order
  if (changes.length === 0 && order.tyreLines.length > 0) {
    const tyre = order.tyreLines.at(orderItemIndex);
    const currentOffer = suggestedOffers.find((suggestedOffer) => {
      if (suggestedOffer.offer.supplier.id !== order.supplierId) {
        return false;
      }
      const orderWarehouseId = tyre?.warehouseId;
      const supplierWarehouseId = suggestedOffer.offer.supplier
        .extendedManufConfiguration?.enabled
        ? suggestedOffer.offer.supplier.extendedManufConfiguration.warehouseId
        : undefined;
      const isExtendedManufOrder =
        !isNil(orderWarehouseId) &&
        !isNil(supplierWarehouseId) &&
        orderWarehouseId === supplierWarehouseId;

      return isExtendedManufOrder === suggestedOffer.offer.extendedManufacturer;
    });
    if (currentOffer) {
      const priceR =
        currentOffer.offer.redistributionSellingPrice === tyre?.priceCustomer
          ? undefined
          : currentOffer.offer.redistributionSellingPrice;
      const estimatedDate =
        currentOffer.offer.deliveryDate &&
        DateUtils.formatTimestamp(new Date(currentOffer.offer.deliveryDate), {
          format: DateUtils.DATE_FORMAT.DMY,
        }) ===
          DateUtils.formatTimestamp(new Date(order.dateDelivered), {
            format: DateUtils.DATE_FORMAT.DMY,
          })
          ? undefined
          : currentOffer.offer.deliveryDate;
      const quantity =
        currentOffer.offer.availableQuantity >= (tyre?.quantity ?? 0)
          ? undefined
          : currentOffer.offer.availableQuantity;

      mappedChanges.set(currentOffer.offerId, {
        ean: currentOffer.tyre.ean,
        supplierId: currentOffer.offer.supplier.id,
        deliveryDate: estimatedDate,
        deliveryDateChanged: !!estimatedDate,
        priceChanged: !!priceR,
        quantityChanged: !!quantity,
        actual: {
          sellingPrice: priceR,
          availableQuantity: quantity,
          deliveryDate: estimatedDate,
        },
      });
      return mappedChanges;
    }
  }
  suggestedOffers.forEach((suggestedOffer) => {
    if (!mappedChanges.has(suggestedOffer.offerId)) {
      const diff = changes.find(
        (change) =>
          change.supplierId === suggestedOffer.offer.supplier.id &&
          change.ean === suggestedOffer.tyre.ean,
      );

      if (
        diff?.priceChanged ||
        diff?.quantityChanged ||
        diff?.deliveryDateChanged
      ) {
        mappedChanges.set(suggestedOffer.offerId, diff);
      }
    }
  });
  return mappedChanges;
};
